import DashboardWidget, { WidgetData } from '../../DashboardWidget';
import { ColorMap } from '../../Reusable/chartConfig';
import axios from '@/_utils/axios';
import { useEffect, useRef, useState, useMemo } from 'react';
import { OrderJobTask } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Fade, Grid, Skeleton, Slide } from '@mui/material';
import TaskCard from './TaskCard';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { selectManualTaskNames, selectTaskStatuses } from '@/redux/selectors/constantsSelectors';
import { selectGraphsRecentTasks, selectGraphsStatus } from '@/redux/selectors/dashboardSelectors';
import SlideToggle from '@/components/_common/SlideToggle';
import { selectUserId } from '@/redux/selectors/authSelectors';
import { setGraphsRecentTasks } from '@/redux/slices/dashboardSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { DashboardConfig } from '@/__configs/config_types';

const HIDDEN_STATUSES = ['cancelled', 'not_ready'];
const INTERVAL = 8000;
const LG_VIEW_BREAKPOINT = 1000;
const MD_VIEW_BREAKPOINT = 650;

const buildBaseViews = (data: OrderJobTask[], colsToDisplay: number) => {
  // More efficient array creation
  const firstRowLength = Math.min(data.length, colsToDisplay);
  const secondRowLength = Math.min(Math.max(0, data.length - colsToDisplay), colsToDisplay);

  return [new Array(firstRowLength).fill(true), new Array(secondRowLength).fill(true)];
};

const RecentTasks = ({ title, caption }: WidgetData) => {
  const dispatch = useAppDispatch();
  const recentTasks = useAppSelector(selectGraphsRecentTasks);
  const [isLoading, setIsLoading] = useState(true);
  const display = useRef(false);
  const slideIn = useRef(true);
  const isHovered = useRef(false);
  const [showPause, setShowPause] = useState(false);
  const [progress, setProgress] = useState(0);
  const taskManualNames = useAppSelector(selectManualTaskNames);
  const taskStatuses = useAppSelector(selectTaskStatuses);
  const timerTracker = useRef(0);
  const firstRun = useRef(true);
  const { editable } = useAppSelector(selectGraphsStatus);
  const setKey = useRef<keyof typeof recentTasks>('all');
  const userId = useAppSelector(selectUserId);
  const [runInterval, setRunInterval] = useState(false);
  const [views, setViews] = useState<boolean[][]>([[], []]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [colsToDisplay, setColsToDisplay] = useState<1 | 2 | 3>(3);
  const recentTasksConfig = useConfigValue<DashboardConfig['recentTasksConfig']>(['dashboardConfig', 'recentTasksConfig']);

  // Memoize calculations to prevent recalculation on every render
  const [currentPage, setCurrentPage] = useState(0);
  const currentPageData = useMemo(() => {
    const tasks = recentTasks[setKey.current] || [];
    const itemsPerPage = colsToDisplay * 2;
    return tasks.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
  }, [recentTasks, setKey.current, currentPage, colsToDisplay]);
  const shouldRunInterval = useMemo(() => (recentTasks[setKey.current] || []).length > 0, [recentTasks, setKey.current]);

  // Combined effect for page resets to reduce re-renders
  useEffect(() => {
    setCurrentPage(0);
    firstRun.current = true;
    display.current = false;
  }, [recentTasks, colsToDisplay, setKey.current]);

  useEffect(() => {
    const tasks = recentTasks[setKey.current] || [];
    const itemsPerPage = colsToDisplay * 2;
    const pageData = tasks.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
    setViews(buildBaseViews(pageData, colsToDisplay));

    if (pageData.length === 0) {
      display.current = false;
    }
  }, [currentPage, recentTasks, setKey.current, colsToDisplay]);

  useEffect(() => {
    if (runInterval !== shouldRunInterval && !editable) {
      setRunInterval(shouldRunInterval);
    }
  }, [shouldRunInterval, editable, runInterval]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (runInterval) {
      timer = setInterval(() => {
        if (!isHovered.current) {
          setShowPause(false);
          timerTracker.current = (timerTracker.current + 200) % INTERVAL;
          setProgress((timerTracker.current / INTERVAL) * 100);

          if (firstRun.current) {
            // On first run, don't transition immediately
            if (timerTracker.current >= 1000) {
              firstRun.current = false;
            }
          } else {
            if (timerTracker.current === 0) {
              slideIn.current = false;
            } else if (timerTracker.current === 400) {
              const tasks = recentTasks[setKey.current] || [];
              const itemsPerPage = colsToDisplay * 2;
              const pageData = tasks.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
              setViews(buildBaseViews(pageData, colsToDisplay));
            } else if (timerTracker.current === 800) {
              setCurrentPage((prev) => (prev + 1) % 3);
              slideIn.current = true;
            }
          }
        } else {
          setShowPause(true);
        }
      }, 200);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [runInterval, recentTasks, setKey.current, colsToDisplay, currentPage]);

  useEffect(() => {
    if (editable) {
      setRunInterval(false);
    } else if (!editable && runInterval !== shouldRunInterval) {
      setRunInterval(shouldRunInterval);
      timerTracker.current = 0;
      setCurrentPage(0);
      display.current = false;
    }
  }, [editable, shouldRunInterval]);

  useEffect(() => {
    const containerElement = containerRef.current;
    let resizeObserver: ResizeObserver | null = null;
    let resizeTimeout: NodeJS.Timeout;

    if (containerElement) {
      resizeObserver = new ResizeObserver((entries) => {
        // Debounce resize events for better performance
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          for (const entry of entries) {
            if (entry.target === containerElement && entry.contentRect.width !== 0) {
              let newCols: 1 | 2 | 3 = 1;

              if (entry.contentRect.width > LG_VIEW_BREAKPOINT) {
                newCols = 3;
              } else if (entry.contentRect.width > MD_VIEW_BREAKPOINT) {
                newCols = 2;
              }

              setColsToDisplay(newCols);
            }
          }
        }, 100); // 100ms debounce
      });

      resizeObserver.observe(containerElement);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      clearTimeout(resizeTimeout);
    };
  }, [containerRef.current]);

  useEffect(() => {
    if (userId && Object.keys(taskStatuses).length > 0) {
      getTasks();
    }
  }, [userId, taskStatuses]);

  const getTasks = async () => {
    setIsLoading(true);
    try {
      const allSet = await axios
        .post(`/core/tasks/search/?page_size=18&page_num=0`, {
          name_filter: Object.keys(taskManualNames),
          status_filter: Object.keys(taskStatuses)?.filter((status) => !HIDDEN_STATUSES.includes(status)),
          show_not_ready: false,
          order_on: 'created_at',
          direction: 'desc',
          ...(recentTasksConfig?.filters || {})
        })
        .then((res) => {
          return res.data.data;
        })
        .catch((err) => {
          console.error('Error retrieving all tasks:', err);
          return [];
        });

      const meSet = await axios
        .post(`/core/tasks/search/?page_size=18&page_num=0`, {
          name_filter: Object.keys(taskManualNames),
          status_filter: Object.keys(taskStatuses)?.filter((status) => !HIDDEN_STATUSES.includes(status)),
          show_not_ready: false,
          order_on: 'created_at',
          direction: 'desc',
          assigned_users_filter: [userId],
          ...(recentTasksConfig?.filters || {})
        })
        .then((res) => {
          return res.data.data;
        })
        .catch((err) => {
          console.error('Error retrieving user tasks:', err);
          return [];
        });

      dispatch(
        setGraphsRecentTasks({
          all: allSet,
          me: meSet
        })
      );

      const itemsPerPage = colsToDisplay * 2;
      setViews(buildBaseViews(allSet.slice(0, itemsPerPage), colsToDisplay));
    } catch (error) {
      console.error('Unexpected error in getTasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleView = (index: number) => {
    const newViews = [...views];
    const rowIndex = Math.floor(index / colsToDisplay);
    const colIndex = index % colsToDisplay;
    newViews[rowIndex] = [...newViews[rowIndex]];
    newViews[rowIndex][colIndex] = !newViews[rowIndex][colIndex];
    setViews(newViews);
  };

  const toggleCardSetKeys = (toggleKey: keyof typeof recentTasks) => {
    setKey.current = toggleKey;
    const newTasks = recentTasks[toggleKey] || [];
    setRunInterval(newTasks.length > 0); // Run interval if we have any tasks
    setCurrentPage(0);
    display.current = false;
    slideIn.current = true;
    timerTracker.current = 0; // reset progression to 0
    const itemsPerPage = colsToDisplay * 2;
    setViews(buildBaseViews(newTasks.slice(0, itemsPerPage), colsToDisplay));
  };

  const showNoDataOverlay = currentPageData.length === 0;

  return (
    <DashboardWidgetWrapped
      title={
        <TitleSection>
          <TitleText>{title}</TitleText>
          {!editable && (
            <FloatingControls>
              <PausedText show={+showPause} disabled={+!runInterval}>
                <span>Paused</span>
                <PolarisIcon height={20} icon="pause" />
              </PausedText>
              <SlideToggleWrapped
                left={{ label: 'All Tasks', value: 'all' }}
                right={{ label: 'Assigned to Me', value: 'me' }}
                callback={(key) => toggleCardSetKeys(key as keyof typeof recentTasks)}
                alternateColors
                defaultUnchecked
                disabled={isLoading}
              />
            </FloatingControls>
          )}
        </TitleSection>
      }
      caption={caption}
      linearProgress={!showNoDataOverlay && runInterval ? { value: progress, variant: 'determinate', color: 'secondary' } : undefined}
    >
      {(_colorMap: ColorMap, loading: boolean) => {
        return loading ? (
          <div style={{ margin: '0 20px' }}>
            <Grid container>
              <GridSkeletonItem item xs={6}>
                <TaskCardSkeleton variant="rectangular" />
              </GridSkeletonItem>
              <GridSkeletonItem item xs={6}>
                <TaskCardSkeleton variant="rectangular" />
              </GridSkeletonItem>
              <GridSkeletonItem item xs={6}>
                <TaskCardSkeleton variant="rectangular" />
              </GridSkeletonItem>
              <GridSkeletonItem item xs={6}>
                <TaskCardSkeleton variant="rectangular" />
              </GridSkeletonItem>
            </Grid>
          </div>
        ) : (
          <TaskCardWrapper
            onMouseEnter={() => {
              isHovered.current = true;
            }}
            onMouseLeave={() => {
              isHovered.current = false;
            }}
          >
            <StyledContainer container ref={containerRef}>
              {showNoDataOverlay && (
                <NoDataOverlay>
                  <Overlay />
                  {isLoading ? (
                    <PolarisIcon
                      height={100}
                      icon="loading"
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        opacity: 0.6
                      }}
                    />
                  ) : (
                    <h3>No Tasks Available</h3>
                  )}
                </NoDataOverlay>
              )}
              {showNoDataOverlay || editable
                ? (colsToDisplay !== 3 ? fakeTasks.slice(0, colsToDisplay === 2 ? 4 : 2) : fakeTasks).map((task, index) => (
                    <StyledGridItem
                      item
                      xs={colsToDisplay === 1 ? 12 : colsToDisplay === 2 ? 6 : 4}
                      key={`recent-task-${index}`}
                      style={{ padding: 10 }}
                      opacity={+(currentPageData.length === 0)}
                    >
                      <TaskCard data={task} viewFront subtitle={'Season 1 Episode 1'} />
                    </StyledGridItem>
                  ))
                : currentPageData.map((task: any, index: number) => (
                    <StyledGridItem item xs={colsToDisplay === 1 ? 12 : colsToDisplay === 2 ? 6 : 4} key={`recent-task-${index}`}>
                      <Slide direction="left" in={slideIn.current} timeout={(index + 1) * 350}>
                        <div style={{ width: '100%', padding: 15 }}>
                          <Fade in={slideIn.current} timeout={(index + 1) * 100}>
                            <div
                              onClick={() => {
                                toggleView(index);
                              }}
                              style={{ minWidth: 300 }}
                            >
                              <TaskCard data={task} viewFront={views[Math.floor(index / colsToDisplay)]?.[index % colsToDisplay] ?? true} />
                            </div>
                          </Fade>
                        </div>
                      </Slide>
                    </StyledGridItem>
                  ))}
            </StyledContainer>
          </TaskCardWrapper>
        );
      }}
    </DashboardWidgetWrapped>
  );
};

export default RecentTasks;

function getRandomDateWithinMonth() {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
  return new Date(start.getTime() + Math.random() * (today.getTime() - start.getTime()));
}

function getRandomFiveDigitNumber() {
  return Math.floor(Math.random() * (99999 - 10000 + 1)) + 10000;
}

const fakeTasks = [
  {
    step: 'awaiting',
    pretty_name: 'edit',
    status: 'edit',
    job: {
      id: getRandomFiveDigitNumber(),
      title: {
        name: 'Polaris'
      },
      due_date: getRandomDateWithinMonth()
    }
  },
  {
    step: 'review',
    pretty_name: 'review',
    status: 'review',
    job: {
      id: getRandomFiveDigitNumber(),
      title: {
        name: 'Polaris'
      },
      due_date: getRandomDateWithinMonth()
    }
  },
  {
    step: 'delivery',
    pretty_name: 'delivery',
    status: 'delivery',
    job: {
      id: getRandomFiveDigitNumber(),
      title: {
        name: 'Polaris'
      },
      due_date: getRandomDateWithinMonth()
    }
  },
  {
    step: 'awaiting',
    pretty_name: 'edit',
    status: 'edit',
    job: {
      id: getRandomFiveDigitNumber(),
      title: {
        name: 'Polaris'
      },
      due_date: getRandomDateWithinMonth()
    }
  },
  {
    step: 'review',
    pretty_name: 'review',
    status: 'review',
    job: {
      id: getRandomFiveDigitNumber(),
      title: {
        name: 'Polaris'
      },
      due_date: getRandomDateWithinMonth()
    }
  },
  {
    step: 'awaiting',
    pretty_name: 'edit',
    status: 'edit',
    job: {
      id: getRandomFiveDigitNumber(),
      title: {
        name: 'Polaris'
      },
      due_date: getRandomDateWithinMonth()
    }
  }
];

const Overlay = styled.div`
  background: ${({ theme }) => theme.palette.background.default};
  opacity: 0.6;
  width: 100%;
  height: 100%;
  z-index: 1;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const GridSkeletonItem = styled(Grid)`
  display: flex;
  flex-direction: row;
`;

const TaskCardSkeleton = styled(Skeleton)`
  height: 200px;
  width: 100%;
  min-width: 300px;
  margin: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const NoDataOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 2;

  h3 {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-style: italic;
    opacity: 0.5;
    font-weight: ${({ theme }) => theme.typography.fontWeightNormal};
    font-style: italic;
    font-size: 1.4rem;
  }
`;

const TaskCardWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing(0, 2, 1)};
  position: relative;
  width: 100%;
`;

const StyledContainer = styled(Grid)`
  height: 450px;
  position: relative;
  justify-content: flex-start;
  align-items: flex-start;
`;

const StyledGridItem = styled(Grid)<{ opacity?: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 325px;
  opacity: ${({ opacity }) => (opacity ? 0.3 : 1)};
`;

const PausedText = styled.div<{ show: number; disabled: number }>`
  margin-left: auto;
  font-style: italic;
  font-size: 1.2rem;
  opacity: 0;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(1)};

  ${({ disabled, show }) =>
    !disabled &&
    `
  transition: opacity 0.3s ease-in-out;
  opacity: ${show ? 0.5 : 0}};
`}
`;

const FloatingControls = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: end;
  flex-flow: row wrap-reverse;
  align-items: center;
  row-gap: ${({ theme }) => theme.spacing(1)};
`;

const TitleSection = styled.div`
  display: flex;
  justify-content: space-between;
  height: 64px;

  .label {
    white-space: nowrap;

    &:last-child {
      padding: ${(props) => props.theme.spacing(0, 1)};
    }
  }
`;

const DashboardWidgetWrapped = styled(DashboardWidget)`
  .MuiCardHeader-subheader {
    margin-top: ${(props) => props.theme.spacing(-4.5)};
  }
`;

const TitleText = styled.div`
  white-space: nowrap;
`;

const SlideToggleWrapped = styled(SlideToggle)`
  margin-left: ${({ theme }) => theme.spacing(3)};
`;
